/**
 * Pure navigation law for the model picker's paged drill-down
 * (providers → accounts → models). The component renders whatever these
 * functions answer; the PK1 oracle runs their stories, so landing, the
 * single-account skip, and every breadcrumb target stay law instead of
 * render-time accidents.
 *
 * @module modelPickerPages
 */
import type { ProviderDriverKind, ProviderInstanceId } from "@cleo/contracts";

import { formatProviderDriverKindLabel } from "../../providerModels";
import type { ProviderInstanceEntry } from "../../providerInstances";

/** 0 = providers, 1 = accounts of one provider, 2 = models of one account. */
export type ModelPickerPageIndex = 0 | 1 | 2;

/** The models page renders one instance's list, or the cross-provider favorites. */
export type ModelPickerRoot = ProviderInstanceId | "favorites";

export interface ModelPickerView {
  readonly page: ModelPickerPageIndex;
  /** Provider context for pages 1 and 2; null on the root page and for favorites. */
  readonly providerKind: ProviderDriverKind | null;
}

/**
 * One provider row on the root page: every configured instance of a driver
 * kind, in the instance display order the caller passed (defaults sort
 * before customs upstream in `deriveProviderInstanceEntries`).
 */
export interface ModelPickerProviderGroup {
  readonly driverKind: ProviderDriverKind;
  readonly label: string;
  readonly entries: ReadonlyArray<ProviderInstanceEntry>;
  readonly modelCount: number;
}

export interface ModelPickerCrumb {
  readonly key: string;
  readonly label: string;
  /** Where the crumb navigates; null marks the current (non-clickable) leaf. */
  readonly target: ModelPickerView | null;
}

/**
 * Group picker-visible instances by driver kind, first appearance order.
 * A provider wears its driver name; account names belong on its accounts page.
 */
export function groupModelPickerProviders(
  entries: ReadonlyArray<ProviderInstanceEntry>,
  modelCountOf: (instanceId: ProviderInstanceId) => number,
): ModelPickerProviderGroup[] {
  const byKind = new Map<ProviderDriverKind, ProviderInstanceEntry[]>();
  for (const entry of entries) {
    const group = byKind.get(entry.driverKind);
    if (group) {
      group.push(entry);
    } else {
      byKind.set(entry.driverKind, [entry]);
    }
  }
  return [...byKind.entries()].map(([driverKind, kindEntries]) => {
    return {
      driverKind,
      label: formatProviderDriverKindLabel(driverKind),
      entries: kindEntries,
      modelCount: kindEntries.reduce((sum, entry) => sum + modelCountOf(entry.instanceId), 0),
    };
  });
}

/**
 * The single-account skip: opening a provider with exactly one configured
 * instance goes straight to that instance's models page — the accounts page
 * exists only when there is an actual account choice to make.
 */
export function resolveProviderOpenTarget(
  group: ModelPickerProviderGroup,
):
  | { readonly kind: "models"; readonly instanceId: ProviderInstanceId }
  | { readonly kind: "accounts" } {
  const only = group.entries.length === 1 ? group.entries[0] : undefined;
  return only ? { kind: "models", instanceId: only.instanceId } : { kind: "accounts" };
}

/**
 * Every opening starts at the grouped root. Driver/continuation locks still
 * apply to every row; they do not replace the person's navigation.
 */
export function resolveModelPickerLanding(input: {
  readonly activeInstanceId: ProviderInstanceId;
  readonly hasFavorites: boolean;
  readonly locked: boolean;
  readonly entries: ReadonlyArray<ProviderInstanceEntry>;
}): { readonly view: ModelPickerView; readonly root: ModelPickerRoot } {
  return { view: { page: 0, providerKind: null }, root: input.activeInstanceId };
}

function providerGroupForRoot(
  root: ModelPickerRoot,
  groups: ReadonlyArray<ModelPickerProviderGroup>,
): ModelPickerProviderGroup | null {
  if (root === "favorites") {
    return null;
  }
  return groups.find((group) => group.entries.some((entry) => entry.instanceId === root)) ?? null;
}

const ROOT_CRUMB: ModelPickerCrumb = {
  key: "providers",
  label: "Models",
  target: { page: 0, providerKind: null },
};

/**
 * Breadcrumb for the current view. The provider crumb of a single-account
 * group is the leaf itself (its accounts page is skipped, so it is never a
 * target); a multi-account group's provider crumb drills to page 1.
 */
export function modelPickerBreadcrumb(input: {
  readonly view: ModelPickerView;
  readonly root: ModelPickerRoot;
  readonly groups: ReadonlyArray<ModelPickerProviderGroup>;
  readonly searching: boolean;
}): ModelPickerCrumb[] {
  if (input.searching) {
    return [ROOT_CRUMB, { key: "search", label: "Search", target: null }];
  }
  if (input.view.page === 0) {
    return [{ ...ROOT_CRUMB, target: null }];
  }
  if (input.view.page === 1) {
    const group = input.groups.find(
      (candidate) => candidate.driverKind === input.view.providerKind,
    );
    return [ROOT_CRUMB, { key: "provider", label: group?.label ?? "Accounts", target: null }];
  }
  if (input.root === "favorites") {
    return [ROOT_CRUMB, { key: "favorites", label: "Favorites", target: null }];
  }
  const group = providerGroupForRoot(input.root, input.groups);
  if (!group) {
    return [ROOT_CRUMB];
  }
  if (group.entries.length === 1) {
    return [ROOT_CRUMB, { key: "provider", label: group.label, target: null }];
  }
  const accountLabel =
    group.entries.find((entry) => entry.instanceId === input.root)?.displayName ?? "Account";
  return [
    ROOT_CRUMB,
    {
      key: "provider",
      label: group.label,
      target: { page: 1, providerKind: group.driverKind },
    },
    { key: "account", label: accountLabel, target: null },
  ];
}

/** One logical level up; null from the root. Mirrors the breadcrumb targets. */
export function modelPickerBackTarget(input: {
  readonly view: ModelPickerView;
  readonly root: ModelPickerRoot;
  readonly groups: ReadonlyArray<ModelPickerProviderGroup>;
}): ModelPickerView | null {
  if (input.view.page === 0) {
    return null;
  }
  if (input.view.page === 1) {
    return { page: 0, providerKind: null };
  }
  const group = providerGroupForRoot(input.root, input.groups);
  if (group && group.entries.length > 1) {
    return { page: 1, providerKind: group.driverKind };
  }
  return { page: 0, providerKind: null };
}

/**
 * Where a page sits relative to the active index — the N-page
 * generalization of the transitions-dev 08 side-by-side recipe: pages left
 * of the active index exit left, pages right exit right.
 */
export function modelPickerPageState(
  pageIndex: number,
  currentPage: number,
): "left" | "active" | "right" {
  if (pageIndex === currentPage) {
    return "active";
  }
  return pageIndex < currentPage ? "left" : "right";
}
